import type { SignalMetadataSchema } from "@repo/app-types/entities/signal";

export interface SignalAssistantSuggestion {
  label: string;
  prompt: string;
}

const REVIEW_SIGNAL_PROMPT = `Review this signal definition for clarity and effectiveness.

Analyze the current signal description and evaluate:
- Is it specific enough to detect the intended pattern without false positives?
- Is it broad enough to capture all relevant variations of the pattern?
- Is the description self-contained and actionable for an AI detector?
- Do the metadata fields look well defined and easy to extract?

If you find areas for improvement, test the current and proposed descriptions against recent customer interactions to validate your recommendations.`;

const DRAFT_DESCRIPTION_PROMPT = `Help me write an effective description for this signal.

Based on the signal name, draft a clear and well-calibrated description that defines exactly what conversational pattern should be detected. The description should:
- Be specific about what language, behaviors, or topics constitute a match
- Include examples of what should and should not match
- Be self-contained so an AI can use it to detect the pattern in call transcripts

Research recent customer interactions to understand what patterns actually occur, then craft a description that accurately captures the intended signal.`;

const SUGGEST_METADATA_PROMPT = `Suggest metadata fields to extract when this signal is detected.

Based on the signal description and recent customer interactions, recommend structured metadata fields that would provide valuable data extraction. Consider:
- Categorical fields (enum) for classifying match subtypes
- Numeric fields for quantifiable aspects of the match

Research recent interactions first to understand what data would be most useful to extract.`;

const TEST_SIGNAL_PROMPT = `Test this signal against recent customer interactions to see how well it performs. Report on:
- How many interactions had matches
- Whether the matches look correct and meaningful
- Any false positives or missed detections you can identify
- Suggestions for improving the signal based on results`;

export function getSignalAssistantSuggestions(args: {
  name: string;
  description: string;
  metadataSchema: SignalMetadataSchema;
}): SignalAssistantSuggestion[] {
  const suggestions: SignalAssistantSuggestion[] = [];

  if (!args.name.trim()) {
    return suggestions;
  }

  if (!args.description.trim()) {
    suggestions.push({
      label: "Draft a description for this signal",
      prompt: DRAFT_DESCRIPTION_PROMPT,
    });
  } else {
    suggestions.push({
      label: "Review this signal for improvements",
      prompt: REVIEW_SIGNAL_PROMPT,
    });

    suggestions.push({
      label: "Test this signal against recent interactions",
      prompt: TEST_SIGNAL_PROMPT,
    });
  }

  if (args.metadataSchema.fields.length === 0) {
    suggestions.push({
      label: "Suggest metadata fields to extract",
      prompt: SUGGEST_METADATA_PROMPT,
    });
  }

  return suggestions;
}
